import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SubtitleCard from "@/components/base/SubtitleCard";
import Pagination from "@/components/base/Pagination";
import SubtitleFilterPanel from "@/components/feature/SubtitleFilterPanel";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import {
  allCategories,
  allSubtitles,
  applyFilters,
  defaultFilters,
  searchSubtitles,
  sortSubtitles,
  type SubtitleFilters,
} from "@/utils/subtitles";

const PAGE_SIZE = 8;

const hotKeywords = ["星际穿越", "三体", "鬼灭之刃", "哪吒", "奥本海默", "中英双语", "霸王别姬"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";

  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState("all");
  const [filters, setFilters] = useState<SubtitleFilters>(defaultFilters);
  const [sort, setSort] = useState("hot");
  const [page, setPage] = useState(1);
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  useDocumentTitle(
    query ? `“${query}” 的搜索结果 - 中文在线字幕免费高清` : "字幕搜索 - 中文在线字幕免费高清",
    "在中文在线字幕免费高清站内搜索电影、电视剧、综艺、动漫的高清中文字幕资源。",
  );

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, filters, sort]);

  const baseList = useMemo(
    () => (query.trim() ? searchSubtitles(query) : allSubtitles),
    [query],
  );

  const resultList = useMemo(() => {
    const byType =
      typeFilter === "all"
        ? baseList
        : baseList.filter((item) => item.typeSlug === typeFilter);
    return sortSubtitles(applyFilters(byType, filters), sort);
  }, [baseList, typeFilter, filters, sort]);

  const totalPages = Math.ceil(resultList.length / PAGE_SIZE);
  const paged = resultList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const runSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setParams(value ? { q: value } : {});
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setTypeFilter("all");
    setSort("hot");
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          字幕资源搜索
        </h1>
        <p className="mt-1.5 text-sm text-foreground-500">
          输入影片名称、类型或关键词，快速查找高清中文字幕
        </p>
        <form onSubmit={runSearch} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：星际穿越、三体、中英双语"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="清空"
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-foreground-500">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => setParams({ q: word })}
              className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <SubtitleFilterPanel
            filters={filters}
            sort={sort}
            onFilterChange={setFilters}
            onSortChange={setSort}
            onReset={resetFilters}
          />
        </aside>

        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              {query.trim() ? (
                <>
                  关键词 <span className="font-semibold text-primary-600">“{query}”</span> 共找到{" "}
                  <span className="font-semibold text-primary-600">{resultList.length}</span> 条结果
                </>
              ) : (
                <>
                  全部字幕资源共{" "}
                  <span className="font-semibold text-primary-600">{resultList.length}</span> 条
                </>
              )}
            </p>
            <button
              type="button"
              onClick={() => setShowMobileFilter((value) => !value)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setTypeFilter("all")}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                typeFilter === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部分类
            </button>
            {allCategories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => setTypeFilter(cat.slug)}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  typeFilter === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {showMobileFilter && (
            <div className="mb-5 lg:hidden">
              <SubtitleFilterPanel
                filters={filters}
                sort={sort}
                onFilterChange={setFilters}
                onSortChange={setSort}
                onReset={resetFilters}
              />
            </div>
          )}

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {paged.map((item) => (
                  <SubtitleCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-search-eye-line text-5xl text-background-500"></i>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">
                没有找到与“{query}”相关的字幕资源
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                换一个关键词试试，或从下面的热门影片中挑选
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => setParams({ q: word })}
                    className="whitespace-nowrap rounded-full bg-background-200 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
                >
                  重置筛选条件
                </button>
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}